<template>
    <div>
        <div v-show="errorMessage.length > 0">
            <div class="text-red-600">
                {{ errorMessage }}
            </div>
            <hr/>
            <router-link :to="{ name: 'database_connections' }" style="color:red;">
                Essayez de connecter votre compte Google à nouveau.
            </router-link>
        </div>

        <div v-show="promisePending && errorMessage.length === 0" class=" absolute top-0 right-0 animate-spin text-center flex justify-center items-center">
            <i class=" fas fa-spinner w-6 h-6"></i>
        </div>
        
        <div v-if="googleAccounts.length > 0">
            <!-- nom du credential airtable -->
            <label for="google_credential">Compte Google</label>
            <b-form-select
                id="google_credential"
                v-model="credentialName" :options="googleAccounts"
                class="mb-2">
            </b-form-select>
        </div>

        <Sheets_import_form
            v-if="isImport && googleFilesSelect.length > 0"
            :credential-name="credentialName"
            :google-files-select="googleFilesSelect"
            :file-sheets="fileSheets"
            :autocomplete-drive-files="autocompleteDriveFiles"

            @importGed="(selectedFile, range, fileName) => importSheetToGED(selectedFile, range, fileName)"
            @importTable="(selectedFile, range, tableName) => importSpreadsheet(selectedFile, range, tableName)"
            @reloadSheets="(selectedFile) => loadSheets(selectedFile)"
            >
        </Sheets_import_form>

        <Sheets_export_form
            v-if="!isImport && googleFilesSelect.length > 0"
            :credential-name="credentialName"
            :google-files-select="googleFilesSelect"
            :file-sheets="fileSheets"
            :autocomplete-drive-files="autocompleteDriveFiles"

            :autocomplete-ged-files="searchGedAutocomplete"

            @updateSheetFromTable="(selectedFile, range, tableName) => updateSheetFromTable(selectedFile, range, tableName)"
            @updateSheetFromGed="(selectedFile, range, gedFile) => updateSheetFromGed(selectedFile, range, gedFile)"

            @reloadSheets="(selectedFile) => loadSheets(selectedFile)"

            @createSheetFromTable="(tableName, sheetName) => createSheetFromTable(tableName, sheetName)"
            @createSheetFromGed="(gedFileName, sheetName) => createSheetFromGed(gedFileName, sheetName)"
        >

        </Sheets_export_form>

    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import google_service from "../../services/google.js";
import Sheets_import_form from './sheets_import_form.vue';
import Sheets_export_form from './sheets_export_form.vue';
import documentService from "../../services/document";

const tableName = ref("");
const credentialName = ref("");

const errorMessage = ref("");
const promisePending = ref(false);

const googleAccounts = ref([]);
const googleFilesSelect = ref([]);
const fileSheets = ref([]);

const driveFileSearch = ref("");
const searchTimeout = ref(null);

const props = defineProps({
    exportMode: {
        type: Boolean,
        required: false,
        default: false
    }
});

const isImport = computed(() => {
    return !props.exportMode;
});

const emits = defineEmits(["validated"]);

const importSheetToGED = async (selectedFile, range, fileName) => {
    promisePending.value = true;
    errorMessage.value = "";

    console.log("Importing sheet to ged");

    let full_credential = credentialName.value;

    let selectedFileObj = googleFilesSelect.value.find((file) => {
        return file.text === selectedFile;
    });

    let res = await google_service.save_sheet_to_ged(selectedFileObj.value, full_credential, range, fileName)
                .catch((err) => {
                    errorMessage.value = "Erreur lors de l'import de la feuille.";
                    promisePending.value = false;
                });

    if (!res) {
        promisePending.value = false;
        return;
    }
    console.log("Got spreadsheet: ", res);

    emits("validated");
}

const importSpreadsheet = async (selectedFile, range, tableName) => {
    console.log("Importing spreadsheet");
    promisePending.value = true;
    errorMessage.value = "";

    let full_credential = credentialName.value;

    let selectedFileObj = googleFilesSelect.value.find((file) => {
        return file.text === selectedFile;
    });

    let res = await google_service.import_spreadsheet(tableName, selectedFileObj.value, full_credential, range)
                .catch((err) => {
                    errorMessage.value = "Erreur lors de l'import de la feuille.";
                    promisePending.value = false;
                });

    if (!res) {
        promisePending.value = false;
        return;
    }
    console.log("Got spreadsheet: ", res);

    emits("validated");
}

const updateSheetFromGed = async (selectedFile, range, gedFile) => {
    promisePending.value = true; 
    errorMessage.value = "";

    let full_credential = credentialName.value;
    let selectedFileObj = googleFilesSelect.value.find((file) => {
        return file.text === selectedFile;
    });

    let res = await google_service.update_sheet_from_ged(gedFile, selectedFileObj.value, full_credential, range)
                .catch((err) => {
                    console.log("Error! ", err);
                    errorMessage.value = "Erreur lors de l'exportation du document.";
                    promisePending.value = false;
                });

    if (!res) {
        promisePending.value = false;
        return;
    }

    emits("validated");
}

const updateSheetFromTable = async (selectedFile, range, tableName) => {
    promisePending.value = true; 
    errorMessage.value = "";

    let full_credential = credentialName.value;
    let selectedFileObj = googleFilesSelect.value.find((file) => {
        return file.text === selectedFile;
    });

    let res = await google_service.update_sheet_from_table(tableName, selectedFileObj.value, full_credential, range)
                .catch((err) => {
                    console.log("Error! ", err);
                    errorMessage.value = "Erreur lors de l'exportation de la feuille.";
                    promisePending.value = false;
                });

    if (!res) {
        promisePending.value = false;
        return;
    }

    emits("validated");
}

const createSheetFromGed = async (gedFile, sheetName) => {
    promisePending.value = true; 
    errorMessage.value = "";

    console.log("Create sheet from file with name: ", gedFile.text);

    let full_credential = credentialName.value;

    let res = await google_service.create_sheet_from_ged(gedFile, full_credential, sheetName)
                .catch((err) => {
                    console.log("Error! ", err);
                    errorMessage.value = "Erreur lors de l'exportation du document.";
                    promisePending.value = false;
                });

    if (!res) {
        promisePending.value = false;
        return;
    }

    emits("validated");
}

const createSheetFromTable = async (tableName, sheetName) => {
    promisePending.value = true; 
    errorMessage.value = "";

    console.log("Create sheet from table with name: ", tableName);

    let full_credential = credentialName.value;

    let res = await google_service.create_sheet_from_table(tableName, full_credential, sheetName)
                .catch((err) => {
                    console.log("Error! ", err);
                    errorMessage.value = "Erreur lors de l'exportation de la table.";
                    promisePending.value = false;
                });

    if (!res) {
        promisePending.value = false;
        return;
    }

    emits("validated");
} 

const loadAccounts = async () => {
    // loadspinner
    errorMessage.value = "";
    promisePending.value = true;

    let accounts = await google_service.getAccounts()
                        .catch((err) => {
                            console.log("Error getting accounts: ", err);
                        });

    console.log("Got accounts: ", accounts);
    if (!accounts) {
        errorMessage.value = "Erreur lors de la récupération des comptes google.";
        promisePending.value = false;
        return;
    }
    accounts = accounts.googleAccounts;

    googleAccounts.value = accounts.map((account) => {
        return {
            value: account.name,
            text: account.name
        }
    });

    credentialName.value = googleAccounts.value[0].value;

    promisePending.value = false;
}

const loadFileNames = async () => {
    // loadspinner
    errorMessage.value = "";
    promisePending.value = true;

    let full_credential = credentialName.value;

    console.log("API searching wityh value: ", driveFileSearch.value);
    let files = await google_service.getDriveFiles("application/vnd.google-apps.spreadsheet", full_credential, driveFileSearch.value) 
                        .catch((err) => {
                            console.log("Error getting files: ", err);
                        });

    console.log("Got files: ", files);
    if (!files) {
        errorMessage.value = "Erreur lors de la récupération des fichiers Drive.";
        promisePending.value = false;
        return;
    }
    files = files.files;

    if (files.length === 0) {
        errorMessage.value = "Aucun fichier Google Sheets trouvé.";
        promisePending.value = false;
        return;
    }

    googleFilesSelect.value = files.map((file) => {
        return {
            value: file.id,
            text: file.name
        }
    });
    // driveFileSearch.value = googleFilesSelect.value[0].text;

    await loadSheets(googleFilesSelect.value[0].text);

    promisePending.value = false;
}

const loadSheets = async (selectedFile) => {
    // loadspinner
    errorMessage.value = "";
    promisePending.value = true;

    let full_credential = credentialName.value;

    let selectedFileObj = googleFilesSelect.value.find((file) => {
        return file.text === selectedFile;
    });

    let sheets = await google_service.getSheets(selectedFileObj.value, full_credential) 
                        .catch((err) => {
                            console.log("Error getting sheets: ", err);
                        });

    console.log("Got sheets: ", sheets);
    if (!sheets) {
        errorMessage.value = "Erreur lors de la récupération des feuilles.";
        promisePending.value = false;
        return;
    }
    sheets = sheets.sheets;

    fileSheets.value = sheets.map((sheet) => {
        return {
            value: sheet.title,
            text: sheet.title
        }
    });

    promisePending.value = false;
}

const autocompleteDriveFiles = async (search) => {

    if (search === driveFileSearch.value) {
        // return existing results
        let filteredSelect = googleFilesSelect.value.filter((file) => {
            return file.text.toLowerCase().includes(driveFileSearch.value.toLowerCase());
        });
        return filteredSelect.map((file) => {
            return file.text;
        });
    }
    driveFileSearch.value = search;

    return new Promise(resolve => {
        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value);
        }
    
        searchTimeout.value = setTimeout(async () => {
            console.log("Searching for: ", search);
            driveFileSearch.value = search;
            await loadFileNames();
            searchTimeout.value = null;
    
            let filteredSelect = googleFilesSelect.value.filter((file) => {
                return file.text.toLowerCase().includes(search.toLowerCase());
            });
        
            let results = filteredSelect.map((file) => {
                return file.text;
            });
        
            console.log("Search results: ", results)
        
            resolve(results);
        }, 500);
    });
}


const gedSearch = ref("");
const gedSearchTimeout = ref(null);
const gedFilesSelect = ref([]);

const searchGed = async () => {
    errorMessage.value = "";
    promisePending.value = true;

    let filter = {
        limit: 10,
        offset: 0,
        order_by: "libelle asc,  path_name, id ",
        rules_sql: `(\`libelle\` LIKE('%${gedSearch.value}%.csv')) and \`type\` = 'file'`
    };

    let response = await documentService.search_documents(null, "", filter)
                        .catch((err) => {
                            console.log("Error getting documents: ", err);
                        });
    if (!response) {
        errorMessage.value = "Erreur lors de la récupération des documents.";
        promisePending.value = false;
        return;
    }

    console.log("Response: ", response);
    if (response.documents.length === 0) {
        errorMessage.value = "Aucun document trouvé.";
        promisePending.value = false;
        return;
    }

    gedFilesSelect.value = response.documents.map((file) => {
        return {
            value: file.id,
            text: file.libelle,
            url: file.url,
            folder: file.folder_name
        }
    });


    promisePending.value = false;
}

const searchGedAutocomplete = async (search) => {
    
    if (search === gedSearch.value) {
        // return existing results
        let filteredSelect = gedFilesSelect.value.filter((file) => {
            return file.text.toLowerCase().includes(gedSearch.value.toLowerCase());
        });
        return filteredSelect.map((file) => {
            return file.text;
        });
    }
    gedSearch.value = search;

    return new Promise(resolve => {
        if (gedSearchTimeout.value) {
            clearTimeout(gedSearchTimeout.value);
        }
    
        gedSearchTimeout.value = setTimeout(async () => {
            console.log("Searching for: ", search);
            gedSearch.value = search;
            // await loadFileNames();
            await searchGed();
            gedSearchTimeout.value = null;
    
            let filteredSelect = gedFilesSelect.value.filter((file) => {
                return file.text.toLowerCase().includes(gedSearch.value.toLowerCase());
            });
        
            // let results = filteredSelect.map((file) => {
            //     return file.text;
            // });
            console.log("Search results: ", filteredSelect)
        
            resolve(filteredSelect);
        }, 500);
    });
}

watch(credentialName, async (newVal, oldVal) => {
    driveFileSearch.value = "";
    await loadFileNames();
    // await loadSheets();
});

onMounted(async () => {
    await loadAccounts();
    // await loadFileNames();
    // await loadSheets();
});

</script>